import { useMemo, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { ApiProvider } from "./api/api-context";
import { ApiError, createApiClient, type ApiClient } from "./api/client";
import { AuthProvider, useAuth } from "./auth/auth-context";
import type { AuthSession, AuthUser } from "./auth/session";
import { createAppRouter } from "./router";

// Server-Daten-Cache. Keine Wiederholung bei 4xx (bleibt falsch), bei
// anderen Fehlern einmal. 503 (Aurora-Resume) wiederholt schon der API-Client.
function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: (failureCount, error) =>
          !(error instanceof ApiError && error.status >= 400 && error.status < 500) &&
          failureCount < 1,
        staleTime: 30_000,
      },
    },
  });
}

// Verbindet Router und Anmeldezustand. Eigene Komponente, weil useAuth() erst
// innerhalb von <AuthProvider> funktioniert.
function RoutedApp({ api, queryClient }: { api: ApiClient; queryClient: QueryClient }) {
  const auth = useAuth();
  // Der Router wird genau einmal erzeugt (useState mit Funktion = nur beim
  // ersten Rendern). Aktuelle Werte reicht RouterProvider über `context` nach.
  const [router] = useState(() => createAppRouter({ auth, api, queryClient }));
  return <RouterProvider router={router} context={{ auth, api, queryClient }} />;
}

export function App({
  session,
  initialUser,
}: {
  session: AuthSession;
  initialUser: AuthUser | null;
}) {
  const [queryClient] = useState(createQueryClient);
  const api = useMemo(
    () => createApiClient({ getAccessToken: () => session.getAccessToken() }),
    [session],
  );
  // Beim Abmelden verlässt der Browser die Seite (session.logout); die App
  // startet danach neu, mit leerem Daten-Cache – der nächste Benutzer sieht nichts.
  return (
    <QueryClientProvider client={queryClient}>
      <ApiProvider api={api}>
        <AuthProvider session={session} initialUser={initialUser}>
          <RoutedApp api={api} queryClient={queryClient} />
        </AuthProvider>
      </ApiProvider>
    </QueryClientProvider>
  );
}
